/// <reference types="vite/client" />
import { redirect } from '@tanstack/react-router';
import { AppError, queryClient } from 'merchant-api';
import { getDetailedOffersOpts } from 'merchant-api/offer';
import { getLDROpts } from 'merchant-api/spiceroad';
import { Application } from 'merchant-api/types';
import { shouldShowProveStep } from './proveStepGate';

/**
 * Single source of truth for "where does this application live?".
 *
 * Two layers, both defined here so routes, the wizard, and the in-wizard polling steps
 * never re-derive status logic locally:
 *  - getAppPlace:    application -> route-level place (apply/renewal wizard, offer, checkout,
 *                    or a terminal place: home / expired / offersExpired).
 *  - getLoanAppStep: application -> wizard step, for the apply/renewal places.
 */

export type AppPlace = 'home' | 'expired' | 'offersExpired' | 'apply' | 'renewal' | 'offer' | 'checkout';

/** The steps of the LoanAppForm wizard (keys of its `steps` map). */
export type LoanAppStep =
  | 'EXPRESS_ESTIMATE'
  | 'PROVE_STEP'
  | 'FINANCE_STEP'
  | 'BUSINESS_CONTACT_STEP'
  | 'BUSINESS_STEP'
  | 'BUSINESS_SELECT'
  | 'OWNER_STEP'
  | 'CONSENT'
  | 'APP_REVIEW'
  | 'BANK_CONFIRMATION'
  | 'BANK_INFO'
  | 'RECEIVED'
  | 'DECLINED'
  | 'PW_SET';

/** Runtime inputs the step mapper needs beyond the application itself. */
export interface LoanStepInputs {
  hasOpenBankRequest?: boolean;
  plaidActiveConnectionEnabled?: boolean;
  /** Applicant is in the M3 test bucket of ece_prove_prefill on a PROVE_STEP channel (see useLoanStepInputs). */
  proveStepEligible?: boolean;
}

// The mapper's form-step outputs (its default branch and the pre-submit estimate): an
// application mapping to one of these has not been submitted yet (the single submit fires
// on APP_REVIEW). Derived from getLoanAppStep's outputs, not a separate status list.
const PRE_SUBMIT_STEPS: LoanAppStep[] = ['EXPRESS_ESTIMATE', 'PROVE_STEP', 'FINANCE_STEP', 'BUSINESS_CONTACT_STEP', 'BUSINESS_STEP', 'OWNER_STEP'];

export const isPreSubmitStep = (step: LoanAppStep) => PRE_SUBMIT_STEPS.includes(step);

/**
 * Map an application's state to the wizard step it belongs on. Any status not explicitly
 * handled is treated as "still filling the intake form" (the wizard's first form step).
 */
export function getLoanAppStep(app: Application, { hasOpenBankRequest, plaidActiveConnectionEnabled, proveStepEligible }: LoanStepInputs = {}): LoanAppStep {
  const expressEstimateFlag = import.meta.env['VITE_SHOW_EXPRESS_ESTIMATE_PAGE'] === 'true';
  const showExpressEstimate = expressEstimateFlag && (app?.properties?.data?.expressEstimateValue ?? 0) > 0 && app?.properties?.data?.showEstimate === true;
  if (showExpressEstimate) return 'EXPRESS_ESTIMATE';

  const isRenewal = app?.type === 'RENEWAL';
  const hasConsent = Boolean(app?.data?.creditConsent && app?.data?.eSignConsent);
  const bankStep = (): LoanAppStep =>
    hasOpenBankRequest === false
      ? 'RECEIVED'
      : plaidActiveConnectionEnabled && isRenewal
        ? 'BANK_CONFIRMATION'
        : 'BANK_INFO';
  const formStep = (): LoanAppStep =>
    isRenewal || app?.metadata?.channelCode === 'PARTNER_STRATEGIC' ? 'BUSINESS_CONTACT_STEP' : 'FINANCE_STEP';

  switch (app?.status) {
    case 'SYSTEM_GENERATED':
    case 'STARTED':
      // Consents are written by ConsentCard immediately before APP_REVIEW, so a pre-submit
      // app that already has them finished the form: an otp_required submit parked it here
      // (Butterfly untouched - it only accepts the owner's token), and re-entering at
      // APP_REVIEW re-fires the full submit now that the caller returned authed from OTP.
      // Without consents the form is still in progress.
      if (hasConsent) return 'APP_REVIEW';
      // PROVE_STEP (XF-10207) is the first intake step for the M3 test bucket, and the gate keys off
      // provePrefillConsent rather than status: status alone cannot tell a fresh application from one
      // whose Prove run already completed, so this one check covers first-visit routing, run-once and
      // the reload-while-STARTED regression together.
      if (shouldShowProveStep(app, proveStepEligible)) return 'PROVE_STEP';
      return formStep();
    case 'SUBMITTED':
      // Under single-submit consent is collected BEFORE the submit, so SUBMITTED means the
      // decision is pending - APP_REVIEW resumes the poll (and never re-submits).
      return 'APP_REVIEW';
    case 'PREPROCESSED':
      // A submitted application without consent still owes the consent step; the bank steps
      // only make sense once consent was given (it is patched onto the app by ConsentCard).
      return hasConsent ? bankStep() : 'CONSENT';
    case 'REROUTED_LEGACY':
    case 'FUNDING':
      // getAppPlace routes FUNDING home first; RECEIVED is just the safe fallback.
      return 'RECEIVED';
    case 'CLOSED_LOST':
    case 'NOT_APPROVED':
      return 'DECLINED';
    case 'APPROVED':
      // Approved renewals continue collecting bank info in the wizard; approved NEW
      // applications live on the offer/checkout routes (getAppPlace) and only land here,
      // on RECEIVED, when no offers exist yet.
      return isRenewal ? bankStep() : 'RECEIVED';
    default:
      return formStep();
  }
}

/**
 * Map an application to its route-level place. Never throws; loaders translate the
 * terminal places with throwForClosedAppPlace, components navigate/error themselves.
 */
export async function getAppPlace(app: Application): Promise<AppPlace> {
  // 'renewal' is the wizard place ('apply') for RENEWAL-type apps — same flow, distinct
  // name so renewal traffic is trackable. Every other place resolves identically.
  const applyPlace: AppPlace = app.type === 'RENEWAL' ? 'renewal' : 'apply';
  // FUNDING = checked out with a delayed funding date; the dashboard shows the banner.
  if (app.status === 'CLOSED_WON' || app.status === 'FUNDING') return 'home';
  if (app.status === 'APPLICATION_DORMANT') return 'expired';
  if (app.status !== 'APPROVED') return applyPlace;

  const detailedOffers = await queryClient.fetchQuery(getDetailedOffersOpts(app.id));
  // Check for expired offers first, then no offers.
  if (detailedOffers?.isExpired) return 'offersExpired';

  const ldrData = app.metadata?.sfOpportunityId ? await queryClient.fetchQuery(getLDROpts(app.metadata.sfOpportunityId)) : undefined;
  if (ldrData?.some((r) => r.classification === 'CLOSING' && (r.status === 'REQUESTED' || r.status === 'MERCHANT_RESPONSE_SUBMITTED'))) {
    return 'checkout';
  }
  if (!detailedOffers?.data?.applicationOffer) return applyPlace;
  if (detailedOffers?.data?.applicationOffer?.selectedOffer) return 'checkout';
  // Divert Pre-approved Renewal (PAR) customers whose offer has NOT been priced
  // to the dashboard ("/")
  if (app.metadata?.referralSource === 'Pre-approved Renewal') return 'home';
  return 'offer';
}

/** Loader-side translation of the terminal places into the redirects/errors routes expect. */
export function throwForClosedAppPlace(place: AppPlace): void {
  if (place === 'home') throw redirect({ to: '/' });
  if (place === 'expired') throw AppError.ApplicationExpired;
  if (place === 'offersExpired') throw AppError.OffersExpired;
}
